// Sidebar Component - Folders, Feeds, and Tags
import { useState, useEffect, useRef, useMemo, Children } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  Rss,
  Tag as TagIcon,
  Plus,
  Star,
  Inbox,
  PanelLeftClose,
  RefreshCcw,
  Loader2,
  Trash2,
  AlertCircle,
  CheckCircle,
  MoreVertical,
  Pencil,
  GripVertical,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
  type DragEndEvent,
  type DropAnimation,
  useDroppable,
  DragStartEvent,
  useDndContext,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api, type Tag } from '@/lib/api';
import Button from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/utils';
import AddFeedDialog from './AddFeedDialog';
import AddFolderDialog from './AddFolderDialog';
import RenameFolderDialog from './RenameFolderDialog';
import RenameTagDialog from './RenameTagDialog';
import ConfirmDialog from '@/components/ConfirmDialog';
import type { ViewFilter } from '../AppLayout';

interface SidebarProps {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
  selectedView: ViewFilter;
  onSelectView: (view: ViewFilter) => void;
  selectedFeedId: number | null;
  onSelectFeed: (id: number | null) => void;
  selectedTagId: number | null;
  onSelectTag: (id: number | null) => void;
}

export default function Sidebar({
  collapsed: _collapsed,
  onToggle,
  selectedView,
  onSelectView,
  selectedFeedId,
  onSelectFeed,
  selectedTagId,
  onSelectTag,
}: SidebarProps) {
  const queryClient = useQueryClient();
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [addFeedDialogOpen, setAddFeedDialogOpen] = useState(false);
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
  const [renameFolderDialogOpen, setRenameFolderDialogOpen] = useState(false);
  const [renameTagDialogOpen, setRenameTagDialogOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState<{ id: number; name: string; color?: string } | null>(null);
  const [tagToRename, setTagToRename] = useState<{ id: number; name: string; color?: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => { },
  });
  const [focusedFeedId, setFocusedFeedId] = useState<number | null>(null);
  const feedRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Helper to parse ID
    const parseId = (id: string) => {
      if (id.startsWith('folder-')) return { type: 'folder', id: parseInt(id.replace('folder-', '')) };
      if (id.startsWith('feed-')) return { type: 'feed', id: parseInt(id.replace('feed-', '')) };
      if (id.startsWith('tag-')) return { type: 'tag', id: parseInt(id.replace('tag-', '')) };
      if (id === 'uncategorized') return { type: 'folder', id: null };
      return { type: 'unknown', id: null };
    };

    const activeItem = parseId(activeId);
    const overItem = parseId(overId);

    const getFeedsForFolder = (folderId: number | null) =>
      feeds.filter((f) => normalizeFolderId(f.folder_id) === normalizeFolderId(folderId));

    // 1. Reorder Folders
    if (activeItem.type === 'folder' && overItem.type === 'folder' && activeItem.id !== null && overItem.id !== null) {
      if (activeId !== overId) {
        const oldIndex = folders.findIndex(f => f.id === activeItem.id);
        const newIndex = folders.findIndex(f => f.id === overItem.id);
        const newFolders = arrayMove(folders, oldIndex, newIndex);

        // Optimistic update
        queryClient.setQueryData(['folders'], { folders: newFolders });

        // API call
        try {
          await api.reorderFolders(newFolders.map(f => f.id));
        } catch (error) {
          console.error('Failed to reorder folders:', error);
          queryClient.invalidateQueries({ queryKey: ['folders'] });
        }
      }
      return;
    }

    // 2. Reorder Tags
    if (activeItem.type === 'tag' && overItem.type === 'tag') {
      if (activeId !== overId) {
        const oldIndex = tags.findIndex(t => t.id === activeItem.id);
        const newIndex = tags.findIndex(t => t.id === overItem.id);
        const newTags = arrayMove(tags, oldIndex, newIndex);

        queryClient.setQueryData(['tags'], { tags: newTags });

        try {
          await api.reorderTags(newTags.map(t => t.id));
        } catch (error) {
          console.error('Failed to reorder tags:', error);
          queryClient.invalidateQueries({ queryKey: ['tags'] });
        }
      }
      return;
    }

    // 3. Handle Feeds (Reorder or Move)
    if (activeItem.type === 'feed') {
      const feed = feeds.find(f => f.id === activeItem.id);
      if (!feed) return;

      // Case A: Dropped on another Feed
      if (overItem.type === 'feed') {
        const overFeed = feeds.find(f => f.id === overItem.id);
        if (!overFeed) return;

        const sourceFolderId = normalizeFolderId(feed.folder_id);
        const targetFolderId = normalizeFolderId(overFeed.folder_id);

        const sourceFeeds = getFeedsForFolder(sourceFolderId);
        const targetFeeds = sourceFolderId === targetFolderId ? sourceFeeds : getFeedsForFolder(targetFolderId);

        // Remove from source list when moving across folders
        const filteredSourceFeeds = sourceFeeds.filter(f => f.id !== feed.id);

        // Insert into target list at overFeed position
        const targetIndex = targetFeeds.findIndex(f => f.id === overFeed.id);
        const updatedTargetFeeds = [
          ...targetFeeds.slice(0, targetIndex),
          { ...feed, folder_id: targetFolderId },
          ...targetFeeds.slice(targetIndex).filter(f => f.id !== feed.id),
        ];

        // Build new global feed order for optimistic UI by replacing the two affected folders
        const folderMap = new Map<number | null, typeof feeds>();
        feeds.forEach((f) => {
          const key = f.folder_id ?? null;
          if (!folderMap.has(key)) folderMap.set(key, []);
          if (f.id !== feed.id) {
            folderMap.get(key)!.push(f);
          }
        });
        folderMap.set(sourceFolderId, filteredSourceFeeds);
        folderMap.set(targetFolderId, updatedTargetFeeds);

        const mergedFeeds: typeof feeds = [];
        folderMap.forEach((list) => mergedFeeds.push(...list));

        queryClient.setQueryData(['feeds'], { feeds: mergedFeeds });

        try {
          // Persist move + order
          await api.updateFeed(feed.id, { folder_id: targetFolderId });
          await api.reorderFeeds(updatedTargetFeeds.map(f => f.id));
          queryClient.invalidateQueries({ queryKey: ['feeds'] });
          queryClient.invalidateQueries({ queryKey: ['folders'] });
          queryClient.invalidateQueries({ queryKey: ['counters'] });
        } catch (error) {
          console.error('Failed to move/reorder feed:', error);
          queryClient.invalidateQueries({ queryKey: ['feeds'] });
        }
      }

      // Case B: Dropped on a Folder (Move to folder and place at end)
      else if (overItem.type === 'folder' || overId === 'uncategorized') {
        const targetFolderId = overItem.id; // null for uncategorized
        if (feed.folder_id !== targetFolderId) {
          const targetFeeds = getFeedsForFolder(targetFolderId);
          const updatedOrder = [...targetFeeds.map(f => f.id), feed.id];

          // Optimistic move
          queryClient.setQueryData(['feeds'], {
            feeds: feeds.map(f => f.id === feed.id ? { ...f, folder_id: targetFolderId } : f),
          });

          try {
            await api.updateFeed(feed.id, { folder_id: targetFolderId });
            if (updatedOrder.length > 0) {
              await api.reorderFeeds(updatedOrder);
            }
            queryClient.invalidateQueries({ queryKey: ['feeds'] });
            queryClient.invalidateQueries({ queryKey: ['folders'] });
            queryClient.invalidateQueries({ queryKey: ['counters'] });
          } catch (error) {
            console.error('Failed to move feed:', error);
            queryClient.invalidateQueries({ queryKey: ['feeds'] });
          }
        }
      }
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  // Fetch data
  const { data: foldersData, isLoading: foldersLoading } = useQuery({
    queryKey: ['folders'],
    queryFn: () => api.getFolders(),
  });

  const { data: feedsData, isLoading: feedsLoading } = useQuery({
    queryKey: ['feeds'],
    queryFn: () => api.getFeeds(),
  });

  const { data: tagsData, isLoading: tagsLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.getTags(),
  });

  const { data: statsData } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => api.getUserStats(),
  });

  const { data: countersData } = useQuery({
    queryKey: ['counters'],
    queryFn: () => api.getCounters(),
  });

  const normalizeFolderId = (id: number | null | undefined) => (id === null || id === undefined ? null : Number(id));

  const folders = foldersData?.folders || [];
  const feeds = feedsData?.feeds || [];
  const tags = tagsData?.tags || [];
  const stats = statsData?.stats;
  const counters = countersData?.counters;

  // Build folder tree for nested folders
  const folderChildren = useMemo(() => {
    const map = new Map<number | null, typeof folders>();
    folders.forEach((folder) => {
      const key = normalizeFolderId(folder.parent_folder_id);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(folder);
    });
    return map;
  }, [folders]);

  const rootFolders = folderChildren.get(null) || [];

  const renderFolderTree = (folder: any, depth: number, sortable: boolean) => {
    const children = (folderChildren.get(folder.id) || []).map((child) =>
      renderFolderTree(child, depth + 1, false)
    );

    return (
      <FolderItem
        key={folder.id}
        folder={folder}
        feeds={feeds}
        expanded={expandedFolders.has(folder.id)}
        onToggle={() => toggleFolder(folder.id)}
        selectedFeedId={selectedFeedId}
        onSelectFeed={(id) => {
          onSelectFeed(id);
          onSelectTag(null);
        }}
        onSelectTag={(id) => onSelectTag(id)}
        onFeedDeleted={(id) => {
          if (selectedFeedId === id) {
            onSelectFeed(null);
          }
        }}
        onRename={() => {
          setFolderToRename({ id: folder.id, name: folder.name, color: folder.color });
          setRenameFolderDialogOpen(true);
        }}
        onDelete={() => {
          const childFolders = folderChildren.get(folder.id) || [];
          const folderFeeds = feeds.filter((f) => normalizeFolderId(f.folder_id) === folder.id);
          const hasChildren = childFolders.length > 0;
          const hasFeeds = folderFeeds.length > 0;
          
          let description = `Delete folder "${folder.name}"?`;
          if (hasChildren && hasFeeds) {
            description += ` This will also delete ${childFolders.length} nested folder${childFolders.length > 1 ? 's' : ''} and move ${folderFeeds.length} feed${folderFeeds.length > 1 ? 's' : ''} to Uncategorized.`;
          } else if (hasChildren) {
            description += ` This will also delete ${childFolders.length} nested folder${childFolders.length > 1 ? 's' : ''}.`;
          } else if (hasFeeds) {
            description += ` ${folderFeeds.length} feed${folderFeeds.length > 1 ? 's' : ''} in this folder will be moved to Uncategorized.`;
          }
          
          setConfirmDialog({
            open: true,
            title: 'Delete Folder',
            description,
            onConfirm: async () => {
              try {
                await api.deleteFolder(folder.id);
                queryClient.invalidateQueries({ queryKey: ['folders'] });
                queryClient.invalidateQueries({ queryKey: ['feeds'] });
                queryClient.invalidateQueries({ queryKey: ['counters'] });
              } catch (error) {
                console.error('Failed to delete folder:', error);
              }
            },
          });
        }}
        getUnread={unreadForFolder}
        getFeedUnread={unreadForFeed}
        focusedFeedId={focusedFeedId}
        setFocusedFeedId={setFocusedFeedId}
        feedRefs={feedRefs}
        setConfirmDialog={setConfirmDialog}
        queryClient={queryClient}
        depth={depth}
        sortable={sortable}
        childrenNodes={children}
      />
    );
  };

  const unreadForFeed = (feedId: number) => {
    const entry = counters?.feeds.find((c) => Number(c.feed_id) === Number(feedId));
    return entry?.unread || 0;
  };

  const unreadForFolder = (folderId: number | null) =>
    counters?.folders.find((c) => c.folder_id === folderId)?.unread || 0;

  const unreadForTag = (tagId: number) =>
    counters?.tags.find((c) => c.tag_id === tagId)?.unread || 0;

  const toggleFolder = (folderId: number) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Get all visible feeds (from expanded folders + uncategorized)
  const getVisibleFeeds = () => {
    const visible: typeof feeds = [];
    // Feeds in expanded folders
    folders
      .filter((f) => !f.parent_folder_id && expandedFolders.has(f.id))
      .forEach((folder) => {
      feeds.filter((f) => normalizeFolderId(f.folder_id) === folder.id).forEach((feed) => visible.push(feed));
    });
    // Uncategorized feeds
    feeds.filter((f) => !f.folder_id).forEach((feed) => visible.push(feed));
    return visible;
  };

  // Keyboard navigation for feeds
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // j/k navigation for feeds
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        const visibleFeeds = getVisibleFeeds();
        if (visibleFeeds.length === 0) return;

        const currentIndex = focusedFeedId
          ? visibleFeeds.findIndex(f => f.id === focusedFeedId)
          : selectedFeedId
            ? visibleFeeds.findIndex(f => f.id === selectedFeedId)
            : -1;

        let nextIndex = currentIndex;
        if (e.key === 'j') {
          nextIndex = currentIndex < visibleFeeds.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : visibleFeeds.length - 1;
        }

        const nextFeed = visibleFeeds[nextIndex];
        if (nextFeed) {
          setFocusedFeedId(nextFeed.id);
          const feedElement = feedRefs.current.get(nextFeed.id);
          feedElement?.focus();
          feedElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      // Enter to select focused feed
      if (e.key === 'Enter' && focusedFeedId) {
        e.preventDefault();
        onSelectFeed(focusedFeedId === selectedFeedId ? null : focusedFeedId);
        setFocusedFeedId(null);
      }

      // Escape to clear focus
      if (e.key === 'Escape') {
        setFocusedFeedId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [feeds, folders, expandedFolders, focusedFeedId, selectedFeedId, onSelectFeed]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-full w-full flex-col">
        {/* Sidebar Header */}
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="font-semibold">Library</h2>
          <Button variant="ghost" size="icon" onClick={() => onToggle(true)} title="Collapse sidebar">
            <PanelLeftClose className="h-5 w-5" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
          {/* Quick Views */}
          <div className="mb-6 space-y-1">
            <button
              onClick={() => {
                onSelectView('all');
                onSelectFeed(null);
                onSelectTag(null);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors',
                selectedView === 'all' && !selectedFeedId && !selectedTagId && 'bg-accent font-medium'
              )}
            >
              <Inbox className="h-4 w-4" />
              <span>All Items</span>
              {stats && <Badge variant="secondary" className="ml-auto">{stats.articles.total}</Badge>}
            </button>
            <button
              onClick={() => {
                onSelectView('unread');
                onSelectFeed(null);
                onSelectTag(null);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors',
                selectedView === 'unread' && !selectedFeedId && !selectedTagId && 'bg-accent font-medium'
              )}
            >
              <Rss className="h-4 w-4" />
              <span>Unread</span>
              {stats && stats.articles.unread > 0 && (
                <Badge variant="default" className="ml-auto">{stats.articles.unread}</Badge>
              )}
            </button>
            <button
              onClick={() => {
                onSelectView('read');
                onSelectFeed(null);
                onSelectTag(null);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors',
                selectedView === 'read' && !selectedFeedId && !selectedTagId && 'bg-accent font-medium'
              )}
            >
              <CheckCircle className="h-4 w-4" />
              <span>Read</span>
              {stats && stats.articles.total > 0 && stats.articles.unread > 0 && (
                <Badge variant="secondary" className="ml-auto">
                  {stats.articles.total - stats.articles.unread}
                </Badge>
              )}
            </button>
            <button
              onClick={() => {
                onSelectView('starred');
                onSelectFeed(null);
                onSelectTag(null);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors',
                selectedView === 'starred' && !selectedFeedId && !selectedTagId && 'bg-accent font-medium'
              )}
            >
              <Star className="h-4 w-4" />
              <span>Starred</span>
              {stats && stats.articles.starred > 0 && (
                <Badge variant="secondary" className="ml-auto">{stats.articles.starred}</Badge>
              )}
            </button>
          </div>

          {/* Folders & Feeds */}
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase text-muted-foreground">Feeds</h3>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  title="Create folder"
                  onClick={() => setAddFolderDialogOpen(true)}
                >
                  <Folder className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  title="Add feed"
                  onClick={() => setAddFeedDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {foldersLoading || feedsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-0.5">
                {/* Root-level folders (draggable) with nested children */}
                <SortableContext items={rootFolders.map(f => `folder-${f.id}`)} strategy={verticalListSortingStrategy}>
                  {rootFolders.map((folder) => renderFolderTree(folder, 0, true))}
                </SortableContext>

                {/* Uncategorized feeds section */}
                <div className="mt-2">
                  <DroppableZone id="uncategorized" className={cn("mb-1 px-2 py-1 rounded-md transition-colors", activeDragId ? "bg-accent/50 border border-dashed border-border" : "")}>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">
                      Uncategorized
                    </div>
                  </DroppableZone>
                  <div className="space-y-1">
                    <SortableContext items={feeds.filter((f) => !f.folder_id).map(f => `feed-${f.id}`)} strategy={verticalListSortingStrategy}>
                      {feeds
                        .filter((f) => !f.folder_id)
                        .map((feed) => (
                          <FeedItem
                            key={feed.id}
                            feed={feed}
                            selected={selectedFeedId === feed.id}
                            focused={focusedFeedId === feed.id}
                            onSelect={() => {
                              const newFeedId = selectedFeedId === feed.id ? null : feed.id;
                              onSelectFeed(newFeedId);
                              onSelectTag(null);
                              setFocusedFeedId(null);
                            }}
                            onDeleteRequest={() => {
                              setConfirmDialog({
                                open: true,
                                title: 'Delete Feed',
                                description: `Delete feed "${feed.title}"? This will remove the feed and all its articles.`,
                                onConfirm: async () => {
                                  try {
                                    await api.deleteFeed(feed.id);
                                    queryClient.invalidateQueries({ queryKey: ['feeds'] });
                                    queryClient.invalidateQueries({ queryKey: ['articles'] });
                                    queryClient.invalidateQueries({ queryKey: ['user-stats'] });
                                    if (selectedFeedId === feed.id) {
                                      onSelectFeed(null);
                                    }
                                  } catch (error) {
                                    console.error('Failed to delete feed:', error);
                                  }
                                },
                              });
                            }}
                            unread={unreadForFeed(feed.id)}
                            feedRef={(el) => {
                              if (el) {
                                feedRefs.current.set(feed.id, el);
                              } else {
                                feedRefs.current.delete(feed.id);
                              }
                            }}
                          />
                        ))}
                    </SortableContext>
                  </div>
                </div>

                {feeds.length === 0 && (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    <Rss className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    <p>No feeds yet</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => setAddFeedDialogOpen(true)}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Add your first feed
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase text-muted-foreground">Tags</h3>
              <Button variant="ghost" size="icon" className="h-6 w-6" title="Create tag">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {tagsLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                <SortableContext items={tags.map(t => `tag-${t.id}`)} strategy={verticalListSortingStrategy}>
                  {tags.map((tag) => (
                    <TagItem
                      key={tag.id}
                      tag={tag}
                      selected={selectedTagId === tag.id}
                      unread={unreadForTag(tag.id)}
                      onSelect={() => onSelectTag(selectedTagId === tag.id ? null : tag.id)}
                      onRename={() => {
                        setTagToRename({ id: tag.id, name: tag.name, color: tag.color });
                        setRenameTagDialogOpen(true);
                      }}
                      onDelete={() => {
                        setConfirmDialog({
                          open: true,
                          title: 'Delete Tag',
                          description: `Delete tag "${tag.name}"? This will remove the tag from all articles.`,
                          onConfirm: async () => {
                            try {
                              await api.deleteTag(tag.id);
                              queryClient.invalidateQueries({ queryKey: ['tags'] });
                              if (selectedTagId === tag.id) {
                                onSelectTag(null);
                              }
                            } catch (error) {
                              console.error('Failed to delete tag:', error);
                            }
                          },
                        });
                      }}
                    />
                  ))}
                </SortableContext>
                {tags.length === 0 && (
                  <p className="px-2 py-1 text-xs text-muted-foreground">No tags yet</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Dialogs */}
        <AddFeedDialog open={addFeedDialogOpen} onOpenChange={setAddFeedDialogOpen} />
        <AddFolderDialog open={addFolderDialogOpen} onOpenChange={setAddFolderDialogOpen} />
        <RenameFolderDialog
          open={renameFolderDialogOpen}
          onOpenChange={setRenameFolderDialogOpen}
          folder={folderToRename}
        />
        <RenameTagDialog
          open={renameTagDialogOpen}
          onOpenChange={setRenameTagDialogOpen}
          tag={tagToRename}
        />
        <ConfirmDialog
          open={confirmDialog.open}
          onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
          title={confirmDialog.title}
          description={confirmDialog.description}
          confirmText="Delete"
          variant="destructive"
          onConfirm={confirmDialog.onConfirm}
        />
        <DragOverlay dropAnimation={dropAnimation}>
          {activeDragId ? (() => {
            const id = String(activeDragId);
            let icon = <Rss className="h-4 w-4" />;
            let label = 'Moving feed...';
            if (id.startsWith('folder-')) {
              icon = <Folder className="h-4 w-4" />;
              label = 'Moving folder...';
            } else if (id.startsWith('tag-')) {
              icon = <TagIcon className="h-4 w-4" />;
              label = 'Moving tag...';
            }
            return (
              <div className="flex items-center gap-2 rounded-md bg-background border border-border p-2 shadow-lg opacity-90 w-[200px]">
                {icon}
                <span className="truncate text-sm font-medium">{label}</span>
              </div>
            );
          })() : null}
        </DragOverlay>
      </div>
    </DndContext>
  );
}

function FolderItem({
  folder,
  feeds,
  childrenNodes,
  depth = 0,
  expanded,
  onToggle,
  selectedFeedId,
  onSelectFeed,
  onSelectTag,
  onFeedDeleted,
  onRename,
  onDelete,
  getUnread,
  getFeedUnread,
  focusedFeedId,
  setFocusedFeedId,
  feedRefs,
  setConfirmDialog,
  queryClient,
  sortable = false,
}: {
  folder: any;
  feeds: any[];
  childrenNodes?: ReactNode;
  depth?: number;
  expanded: boolean;
  onToggle: () => void;
  selectedFeedId: number | null;
  onSelectFeed: (id: number | null) => void;
  onSelectTag: (id: number | null) => void;
  onFeedDeleted: (id: number) => void;
  onRename: () => void;
  onDelete: () => void;
  getUnread: (id: number | null) => number;
  getFeedUnread: (id: number) => number;
  focusedFeedId: number | null;
  setFocusedFeedId: (id: number | null) => void;
  feedRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
  setConfirmDialog: (dialog: { open: boolean; title: string; description: string; onConfirm: () => void }) => void;
  queryClient: any;
  sortable?: boolean;
}) {
  const normalizeFolderId = (id: number | null | undefined) => (id === null || id === undefined ? null : Number(id));
  const folderId = normalizeFolderId(folder.id) ?? folder.id;
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const unread = getUnread(folderId);

  const sortableId = `folder-${folderId}`;
  const folderFeeds = feeds.filter((f) => normalizeFolderId(f.folder_id) === folderId);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = sortable
    ? useSortable({ id: sortableId })
    : { attributes: {}, listeners: {}, setNodeRef: undefined, setActivatorNodeRef: undefined, transform: null, transition: undefined, isDragging: false };

  const { over } = useDndContext();
  const isOver = over?.id === sortableId;

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    transition,
  } : undefined;
  const combinedStyle = {
    marginLeft: depth * 12,
    ...(style || {}),
  };

  return (
    <div
      ref={sortable ? setNodeRef : undefined}
      style={combinedStyle}
      className="space-y-0"
    >
      <DroppableZone
        id={sortableId}
        className={cn(
          "group rounded-md transition-colors flex items-center gap-1",
          isOver ? "bg-accent/50 ring-1 ring-primary" : "",
          isDragging ? "opacity-50" : ""
        )}
      >
        <button
          ref={sortable ? setActivatorNodeRef : undefined}
          {...(sortable ? { ...attributes, ...listeners } : {})}
          onClick={() => onToggle()}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuPos({ x: e.clientX, y: e.clientY });
            setShowMenu(true);
          }}
          className="flex flex-1 items-center gap-1 rounded-md px-2 py-1 text-sm hover:bg-accent transition-colors text-left cursor-pointer"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Folder className="h-4 w-4" style={{ color: folder.color }} />
          <span className="flex-1 text-left">{folder.name}</span>
          <Badge variant={unread > 0 ? 'default' : 'secondary'} className="text-xs shrink-0">
            {unread > 0 ? (unread > 99 ? '99+' : unread) : folderFeeds.length}
          </Badge>
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 opacity-30 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setMenuPos({ x: rect.right - 160, y: rect.bottom + 4 });
            setShowMenu(true);
          }}
          title="Folder options (or right-click)"
          // Prevent drag when clicking menu
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={(e) => {
                e.stopPropagation(); // Stop propagation to prevent drag?
                setShowMenu(false);
              }}
            />
            <div
              className="fixed z-50 min-w-[160px] rounded-md border border-border bg-popover p-1 shadow-md"
              style={{ left: menuPos.x, top: menuPos.y }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                onClick={(e) => {
                  e.stopPropagation();
                  onRename();
                  setShowMenu(false);
                }}
              >
                <Pencil className="h-4 w-4" />
                <span>Rename</span>
              </button>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowMenu(false);
                }}
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete</span>
              </button>
            </div>
          </>
        )}
      </DroppableZone>
      {expanded && (
        <div className="space-y-0 pl-4 mt-0">
          <SortableContext items={folderFeeds.map(f => `feed-${f.id}`)} strategy={verticalListSortingStrategy}>
            {folderFeeds.map((feed) => (
              <FeedItem
                key={feed.id}
                feed={feed}
                selected={selectedFeedId === feed.id}
                focused={focusedFeedId === feed.id}
                onSelect={() => {
                  const newFeedId = selectedFeedId === feed.id ? null : feed.id;
                  onSelectFeed(newFeedId);
                  onSelectTag(null);
                  setFocusedFeedId(null);
                }}
                onDeleteRequest={() => {
                  setConfirmDialog({
                    open: true,
                    title: 'Delete Feed',
                    description: `Delete feed "${feed.title}"? This will remove the feed and all its articles.`,
                    onConfirm: async () => {
                      try {
                        await api.deleteFeed(feed.id);
                        queryClient.invalidateQueries({ queryKey: ['feeds'] });
                        queryClient.invalidateQueries({ queryKey: ['articles'] });
                        queryClient.invalidateQueries({ queryKey: ['user-stats'] });
                        onFeedDeleted(feed.id);
                      } catch (error) {
                        console.error('Failed to delete feed:', error);
                      }
                    },
                  });
                }}
                unread={getFeedUnread(feed.id)}
                feedRef={(el) => {
                  if (el) {
                    feedRefs.current.set(feed.id, el);
                  } else {
                    feedRefs.current.delete(feed.id);
                  }
                }}
              />
            ))}
          </SortableContext>
          {childrenNodes && Children.count(childrenNodes) > 0 && (
            <div className="pl-3">{childrenNodes}</div>
          )}
        </div>
      )}
    </div>
  );
}

function TagItem({
  tag,
  selected,
  unread,
  onSelect,
  onRename,
  onDelete,
}: {
  tag: Tag;
  selected: boolean;
  unread: number;
  onSelect: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `tag-${tag.id}` });

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    transition,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-all duration-200",
        selected ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-accent hover:text-foreground",
        isDragging ? "opacity-50" : ""
      )}
      onClick={onSelect}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenuPos({ x: e.clientX, y: e.clientY });
        setShowContextMenu(true);
      }}
      {...attributes}
      {...listeners}
    >
      <TagIcon className="h-4 w-4 shrink-0" style={{ color: tag.color }} />
      <span className="truncate flex-1">{tag.name}</span>
      {unread > 0 && (
        <Badge variant="secondary" className="ml-auto text-[10px] h-5 px-1.5 min-w-[1.25rem] justify-center">
          {unread > 99 ? '99+' : unread}
        </Badge>
      )}

      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              setShowContextMenu(false);
            }}
          />
          <div
            className="fixed z-50 min-w-[160px] rounded-md border border-border bg-popover p-1 shadow-md"
            style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={(e) => {
                e.stopPropagation();
                onRename();
                setShowContextMenu(false);
              }}
            >
              <Pencil className="h-4 w-4" />
              <span>Rename</span>
            </button>
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                setShowContextMenu(false);
              }}
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete Tag</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FeedItem({
  feed,
  selected,
  focused,
  onSelect,
  unread,
  feedRef,
  onDeleteRequest,
}: {
  feed: any;
  selected: boolean;
  focused?: boolean;
  onSelect: () => void;
  unread: number;
  feedRef?: (el: HTMLDivElement | null) => void;
  onDeleteRequest: () => void;
}) {
  const queryClient = useQueryClient();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [faviconError, setFaviconError] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `feed-${feed.id}` });

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    transition,
  } : undefined;

  const refreshFeed = useMutation({
    mutationFn: () => api.refreshFeed(feed.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['feeds'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      queryClient.invalidateQueries({ queryKey: ['counters'] });
    },
  });


  const hasError = feed.status === 'error';
  const errorMessage = feed.last_error || 'Feed fetch failed';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("group flex items-center gap-1", isDragging ? "opacity-50" : "")}
    >
      <button
        {...listeners}
        {...attributes}
        className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-3 w-3" />
      </button>
      <div
        ref={feedRef}
        className={cn(
          'flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-all duration-200 min-w-0',
          'hover:bg-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
          selected && 'bg-accent font-medium shadow-sm border-l-2 border-l-primary',
          focused && !selected && 'bg-accent/50 ring-1 ring-primary/50',
          hasError && 'border-l-2 border-l-destructive'
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSelect();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setContextMenuPos({ x: e.clientX, y: e.clientY });
          setShowContextMenu(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 pr-14">
          {/* Feed favicon or icon with error indicator */}
          <div className="relative shrink-0 flex items-center justify-center">
            {feed.favicon_url && !faviconError ? (
              <img
                src={feed.favicon_url}
                alt=""
                className={cn(
                  "h-3 w-3 rounded-sm object-cover",
                  hasError && "opacity-50"
                )}
                onError={() => setFaviconError(true)}
              />
            ) : (
              <Rss className={cn(
                "h-3 w-3",
                hasError ? "text-destructive" : "text-muted-foreground",
                selected && !hasError && "text-primary"
              )} />
            )}
            {hasError && (
              <AlertCircle className="absolute -top-1 -right-1 h-2 w-2 text-destructive fill-destructive" />
            )}
          </div>
          <span className={cn(
            "truncate",
            selected && "font-semibold",
            hasError && "text-destructive/90"
          )}>{feed.title}</span>
          {hasError && (
            <div className="relative group/error ml-auto shrink-0">
              <AlertCircle className="h-3 w-3 text-destructive shrink-0" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover/error:block z-50">
                <div className="bg-popover text-popover-foreground text-xs rounded-md px-2 py-1 shadow-md border border-border whitespace-nowrap">
                  <div className="font-semibold mb-1">Feed Error</div>
                  <div className="text-muted-foreground">{errorMessage}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">Click refresh to retry</div>
                </div>
              </div>
            </div>
          )}
          <Badge
            variant={unread > 10 ? "default" : "secondary"}
            className={cn(
              "ml-auto text-[10px] shrink-0",
              unread > 10 && "font-semibold",
              unread === 0 && "opacity-60"
            )}
          >
            {unread > 99 ? '99+' : unread}
          </Badge>
        </div>
        <div className="flex items-center gap-0.5 shrink-0 w-[56px] justify-end -mr-2">
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-7 w-7 transition-opacity inline-flex opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto opacity-60 hover:opacity-100",
              showContextMenu && "opacity-100 pointer-events-auto"
            )}
            onClick={(e) => {
              e.stopPropagation();
              refreshFeed.mutate();
            }}
            title={hasError ? `Retry: ${errorMessage}` : "Refresh feed now"}
            disabled={refreshFeed.isPending}
          >
            {refreshFeed.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className={cn("h-4 w-4", hasError && "text-destructive")} />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 transition-opacity inline-flex opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto opacity-60 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteRequest();
            }}
            title="Delete feed"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowContextMenu(false)}
          />
          <div
            className="fixed z-50 min-w-[180px] rounded-md border border-border bg-popover p-1 shadow-md"
            style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={(e) => {
                e.stopPropagation();
                refreshFeed.mutate();
                setShowContextMenu(false);
              }}
              disabled={refreshFeed.isPending}
            >
              {refreshFeed.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCcw className="h-4 w-4" />
              )}
              <span>Refresh Feed</span>
            </button>
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRequest();
                setShowContextMenu(false);
              }}
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete Feed</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function DroppableZone({ id, children, className }: { id: string | number; children: React.ReactNode; className?: string }) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div ref={setNodeRef} className={cn(className, isOver ? "bg-accent/50 ring-1 ring-primary" : "")}>
      {children}
    </div>
  );
}
